import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { flightService } from '../../services/flightService';
import BeforeYouBook from '../../components/BeforeYouBook';

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/assets/images/no_aircraft.png';

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative S3 path (aircraft-pics/...), construct S3 URL
  if (imageUrl.startsWith('aircraft-pics/')) {
    // Use S3 bucket URL directly
    return `https://pjo-uploads.s3.ap-southeast-1.amazonaws.com/${imageUrl}`;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  return `${baseUrl}/${imageUrl}`;
};

const FlightDetailsPage = ({ hideBreadcrumb = false, id: propId, onNext, passengers: propPassengers }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();
  const location = useLocation();
  const [flightData, setFlightData] = useState(null);
  const [aircraftImages, setAircraftImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBeforeYouBook, setShowBeforeYouBook] = useState(false);

  // Get passengers and route type from session/location state
  const totalPassengers = parseInt(propPassengers || location.state?.passengers || sessionStorage.getItem('total_passengers') || 1);
  const routeType = location.state?.routeType || sessionStorage.getItem('route_type') || 'One Way';

  useEffect(() => {
    // Store in session
    sessionStorage.setItem('total_passengers', totalPassengers);
    sessionStorage.setItem('route_type', routeType);
    sessionStorage.setItem('flight-list-Url', location.state?.fromUrl || '/search-flight');

    fetchFlightDetails();
  }, [id]);

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);

      // Single flight (one-way) - this component only handles one-way flights
      const response = await flightService.getFlightDetails(id);

      console.log('✈️ Flight Details Response:', response);

      if (response.success) {
        setFlightData(response.data);
        const images = response.data.aircraft_images || [];
        console.log('🖼️ Aircraft Images:', images);
        setAircraftImages(images);
      } else {
        setError('Flight not found');
      }
    } catch (err) {
      console.error('Failed to load flight details:', err);
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = () => {
    if (!flightData) return { hours: 0, minutes: 0 };

    const departure = new Date(flightData.departure.datetime);
    const arrival = new Date(flightData.arrival.datetime);
    const diffMs = arrival - departure;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return { hours, minutes };
  };

  const calculatePricing = () => {
    if (!flightData) return { farePerPax: 0, totalFare: 0, blockFare: 0, isSeatPricing: false };

    let farePerPax = 0;
    let totalFare = 0;
    let blockFare = 0;
    let isSeatPricing = false;

    if (flightData.block_price && flightData.seat_price) {
      isSeatPricing = true;
      farePerPax = parseFloat(flightData.seat_price);

      if (flightData.commission_fee) {
        farePerPax = Math.round(farePerPax + (farePerPax * flightData.commission_fee / 100));
      } else {
        farePerPax = Math.round(farePerPax);
      }

      totalFare = farePerPax * totalPassengers;
    } else {
      blockFare = parseFloat(flightData.block_empty_price || 0);

      if (flightData.commission_fee) {
        blockFare = Math.round(blockFare + (blockFare * flightData.commission_fee / 100));
      } else {
        blockFare = Math.round(blockFare);
      }
    }

    return { farePerPax, totalFare, blockFare, isSeatPricing };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDateTime = (datetime, format = 'time') => {
    const date = new Date(datetime);

    if (format === 'time') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } else if (format === 'date') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;
    } else if (format === 'full') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
  };

  const handleNext = () => {
    const pricing = calculatePricing();
    const remainingSeats = flightData.remaining_seats;

    // Check seat availability for seat pricing
    if (pricing.isSeatPricing) {
      if (remainingSeats === 0) {
        alert('There is no seat available on this flight.');
        return;
      } else if (remainingSeats < totalPassengers) {
        alert(`Only ${remainingSeats} seats available in this flight please select other flight`);
        return;
      }
    }

    // If inside FlightBookingFlow (onNext callback provided), call it to switch to passengers tab
    if (onNext) {
      onNext(pricing);
    } else {
      // Otherwise navigate to add passengers page directly
      navigate(`/add-passengers/${id}`, {
        state: {
          flightData,
          totalPassengers,
          routeType,
          pricing
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <img src="/assets/images/flight_logo.gif" alt="Loading..." />
      </div>
    );
  }

  if (error || !flightData) {
    return (
      <div className="container mx-auto px-4 mt-5">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error || 'Flight not found'}</div>
      </div>
    );
  }

  const duration = calculateDuration();
  const pricing = calculatePricing();

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Breadcrumb - Hidden when used inside FlightBookingFlow */}
      {!hideBreadcrumb && (
        <div className="bg-white shadow-sm">
          <div className="flex flex-wrap justify-center mx-auto gap-2 w-full py-2">
            <a href="/search-flight" className="no-underline">
              <div className="mr-4">
                <div className="w-30 h-10 border-b-2 border-gray-400 pr-7 pl-0.5">
                  <span className="text-2xl">1</span>
                  <span className="text-xs text-gray-600 ml-2">Home</span>
                </div>
              </div>
            </a>

            <a href={sessionStorage.getItem('flight-list-Url') || '/search-flight'} className="no-underline">
              <div className="mr-4">
                <div className="w-30 h-10 border-b-2 border-gray-400 pr-7 pl-0.5">
                  <span className="text-2xl">2</span>
                  <span className="text-xs text-gray-600 ml-2">Flights</span>
                </div>
              </div>
            </a>

            <a href="javascript:void(0);" className="no-underline">
              <div className="mr-4">
                <div className="w-30 h-10 border-b-2 border-yellow-400 pr-7 pl-0.5">
                  <span className="text-2xl text-yellow-400">3</span>
                  <span className="text-xs text-gray-600 ml-2">Details</span>
                </div>
              </div>
            </a>

            <a href="javascript:void(0);" className="no-underline">
              <div className="mr-4">
                <div className="w-30 h-10 border-b-2 border-gray-400 pr-7 pl-0.5">
                  <span className="text-2xl">4</span>
                  <span className="text-xs text-gray-600 ml-2">Passengers</span>
                </div>
              </div>
            </a>

            <a href="javascript:void(0);" className="no-underline">
              <div className="mr-4">
                <div className="w-30 h-10 border-b-2 border-gray-400 pr-7 pl-0.5">
                  <span className="text-2xl">5</span>
                  <span className="text-xs text-gray-600 ml-2">Payment</span>
                </div>
              </div>
            </a>
          </div>
        </div>
      )}

      {/* Main Content */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded shadow mb-0">
            <div className="p-5">
              <div className="flex flex-wrap -mx-4">
                <div className="w-full px-4">
                  <div className="bg-white rounded overflow-hidden mb-0">
                    <div className="container mx-auto px-4 pt-3 pb-3">
                      <div className="flex flex-wrap -mx-4">
                        {/* Aircraft Image */}
                        <div className="w-full xl:w-1/4 md:w-1/3 px-4">
                          <div className="w-full">
                            {aircraftImages.length > 0 ? (
                              <img
                                src={getImageUrl(aircraftImages[0].presigned_url || aircraftImages[0].url)}
                                alt={aircraftImages[0].name}
                                className="w-full h-auto object-cover rounded"
                                onError={(e) => {
                                  e.currentTarget.src = '/assets/images/no_aircraft.png';
                                }}
                              />
                            ) : (
                              <img
                                src="/assets/images/no_aircraft.png"
                                alt="Aircraft"
                                className="w-full h-auto object-cover rounded"
                              />
                            )}
                          </div>
                        </div>

                        {/* Flight Details */}
                        <div className="w-full xl:w-3/4 md:w-2/3 px-4">
                          <div className="pt-2">
                            <div className="py-2">
                              <p className="text-lg text-gray-800 font-bold m-0">
                                {flightData.flight_number.toUpperCase()}
                              </p>
                              <div className="py-2">
                                <div className="flex flex-wrap -mx-4">
                                  <div className="w-full px-4">
                                    <div className="flex flex-wrap -mx-4">
                                      <div className="w-full md:w-1/3 px-4">
                                        <h4 className="text-sm font-bold text-gray-600 mb-0">
                                          {flightData.departure.airport_code} {formatDateTime(flightData.departure.datetime, 'time')}
                                        </h4>
                                        <p className="mb-0">
                                          {formatDateTime(flightData.departure.datetime, 'date')}
                                        </p>
                                      </div>

                                      <div className="w-full md:w-1/3 px-4 text-center">
                                        <span>→</span>
                                        <p className="mb-0">{duration.hours} Hrs {duration.minutes} Mins</p>
                                      </div>

                                      <div className="w-full md:w-1/3 px-4 text-right">
                                        <h4 className="text-sm font-bold text-gray-600 mb-0">
                                          {flightData.arrival.airport_code} {formatDateTime(flightData.arrival.datetime, 'time')}
                                        </h4>
                                        <p className="mb-0">
                                          {formatDateTime(flightData.arrival.datetime, 'date')}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex flex-wrap -mx-4">
                                  <div className="w-full md:w-7/12 px-4">
                                    <div className="flex flex-col gap-1">
                                      <p className="mb-0 text-sm">Available Seats: <span className="font-bold">{flightData.seat_available}</span></p>
                                    </div>

                                    {pricing.isSeatPricing ? (
                                      <p className="mb-0 font-bold text-black-600 text-sm">
                                        Fare/Pax ${formatCurrency(pricing.farePerPax)}
                                        &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                        Total Fare ${formatCurrency(pricing.totalFare)}
                                      </p>
                                    ) : (
                                      <p className="mb-0 font-bold text-black-600 text-sm">
                                        Block Fare ${formatCurrency(pricing.blockFare)}
                                      </p>
                                    )}
                                  </div>
                                  <div style={{ paddingTop: '20px' }} className="w-full md:w-5/12 px-4 text-right">
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setShowBeforeYouBook(!showBeforeYouBook);
                                      }}
                                      className="font-bold text-gray-600 text-sm no-underline"
                                    >
                                      Before You Book
                                      <i className={`fa fa-caret-${showBeforeYouBook ? 'up' : 'down'} ml-2`}></i>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Before You Book Section */}
                    <BeforeYouBook
                      isOpen={showBeforeYouBook}
                      onToggle={() => setShowBeforeYouBook(!showBeforeYouBook)}
                    />
                  </div>
                </div>
              </div>

              {/* Flight Summary Card */}
              <div className="w-full px-4">
                <div className="bg-white rounded shadow border border-gray-200 mb-0">
                  <div className="bg-yellow-400 border-b border-gray-200 px-5 py-4">
                    <h5 className="text-base font-bold m-0">Flight Summary</h5>
                  </div>
                  <div className="p-0 border-t-0">
                    <div className="p-5 pt-4 pb-2" style={{ paddingLeft: '0px', paddingRight: '0px' }}>
                      <div className="overflow-x-auto px-2">
                        <table className="w-full border border-gray-200 mb-0 whitespace-nowrap border-t" style={{ border: '1px solid #dee2e6' }}>
                          <thead>
                            <tr style={{ border: '1px solid #dee2e6' }}>
                              <th className="border-b-0 py-2 text-black-600 px-3 text-left" style={{ border: '1px solid #dee2e6' }}>Flight Number</th>
                              <th className="border-b-0 py-2 text-black-600 px-3 text-left" style={{ border: '1px solid #dee2e6' }}>Departure</th>
                              <th className="border-b-0 py-2 text-black-600 px-3 text-left" style={{ border: '1px solid #dee2e6' }}>ETD</th>
                              <th className="border-b-0 py-2 text-black-600 px-3 text-left" style={{ border: '1px solid #dee2e6' }}>Arrival</th>
                              <th className="border-b-0 py-2 text-black-600 px-3 text-left" style={{ border: '1px solid #dee2e6' }}>ETA</th>
                              <th className="border-b-0 py-2 text-black-600 px-3 text-left" style={{ border: '1px solid #dee2e6' }}>Flight Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ border: '1px solid #dee2e6' }}>
                              <td className="py-2 px-3" style={{ border: '1px solid #dee2e6' }}>
                                <span style={{ textTransform: 'capitalize' }}>
                                  {flightData.flight_number.toUpperCase()}
                                </span>
                              </td>
                              <td className="py-2 px-3" style={{ border: '1px solid #dee2e6' }}>
                                <span style={{ textTransform: 'capitalize' }}>
                                  {flightData.departure.airport_code}
                                </span>
                              </td>
                              <td className="py-2 px-3" style={{ border: '1px solid #dee2e6' }}>
                                {formatDateTime(flightData.departure.datetime, 'full')}
                              </td>
                              <td className="py-2 px-3" style={{ border: '1px solid #dee2e6' }}>
                                <span style={{ textTransform: 'capitalize' }}>
                                  {flightData.arrival.airport_code}
                                </span>
                              </td>
                              <td className="py-2 px-3" style={{ border: '1px solid #dee2e6' }}>
                                {formatDateTime(flightData.arrival.datetime, 'full')}
                              </td>
                              <td className="py-2 px-3" style={{ border: '1px solid #dee2e6' }}>
                                {duration.hours} Hrs {duration.minutes} Mins
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <ul className="list-none p-0 m-0 border-b border-dashed border-gray-300 mb-0">
                      <li className="px-5 py-3 border-b border-dashed border-gray-400">
                        <div className="flex justify-between w-full">
                          <p className="mb-0 text-black-600 font-bold">Fare</p>
                          <b className="text-black-600">Amount</b>
                        </div>
                      </li>
                      <li className="px-5 py-3 border-b border-dashed border-gray-400">
                        <div className="flex justify-between w-full">
                          <p className="mb-0">Total Flight Time</p>
                          <b className="text-gray-600">
                            {duration.hours} Hrs {duration.minutes} Mins
                          </b>
                        </div>
                      </li>
                      <li className="px-5 py-3 border-b border-dashed border-gray-400">
                        <div className="flex justify-between w-full">
                          <p className="mb-0">Add-ons</p>
                          <b className="text-gray-600">$ 0</b>
                        </div>
                      </li>
                      <li className="px-5 py-3 border-b border-dashed border-gray-400">
                        <div className="flex justify-between w-full">
                          <p className="mb-0 text-black-600 font-bold">Total Fare</p>
                          <b className="text-black-600">
                            {pricing.isSeatPricing
                              ? `$ ${formatCurrency(pricing.totalFare)}`
                              : `$ ${formatCurrency(pricing.blockFare)}`
                            }
                          </b>
                        </div>
                      </li>
                    </ul>

                    <div className="p-5 text-right py-3">
                      <button
                        className="inline-block px-5 py-2 text-sm font-bold text-center rounded cursor-pointer transition-all bg-yellow-400 text-black hover:bg-yellow-500 shadow-none"
                        onClick={handleNext}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section >
    </div >
  );
};

export default FlightDetailsPage;